import axios from "axios";
import {
  off,
  onChildAdded,
  onChildChanged,
  push,
  ref,
  serverTimestamp,
} from "firebase/database";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

import { trackMessageSent } from "../analytics/events";
import { myAuth, myFirestore, myRtdb } from "../contexts/firebaseProvider";

import logger from "utils/logger";

// Iniciar ou retornar chat existente
export async function startChat(targetUserId) {
  // Agentes de IA desligados na API: a marcação isAI/agentParticipantId que
  // este trecho gravava no RTDB não tem mais consumidor, e perfis-agente não
  // aparecem mais nas listagens. Ver o bloco "AGENTES DE IA — DESLIGADOS" em
  // isugar-api/functions/index.js.
  return axios.post(`/v1/chat/start`, { targetUserId });
}

// Listar chats do usuário
export function listenToUserChats(callback, errorCallback) {
  const currentUserId = myAuth.currentUser.uid;
  logger.log("chatService: Configurando listener para usuário:", currentUserId);

  const chatsRef = collection(
    myFirestore,
    "user_chats",
    currentUserId,
    "chats",
  );
  const q = query(chatsRef, orderBy("lastMessageTimestamp", "desc"));

  return onSnapshot(
    q,
    (snapshot) => {
      logger.log("chatService: Snapshot recebido, documentos:", snapshot.size);
      const chats = [];
      const now = Date.now();

      snapshot.forEach((doc) => {
        const chatData = doc.data();
        const lastMessageTimestamp = chatData.lastMessageTimestamp;

        // Determina o tipo baseado na última atualização
        let type = "older";
        if (lastMessageTimestamp) {
          // Converte Firestore Timestamp para milissegundos se necessário
          const timestampMs = lastMessageTimestamp.toMillis
            ? lastMessageTimestamp.toMillis()
            : lastMessageTimestamp;

          const daysDifference = (now - timestampMs) / (24 * 60 * 60 * 1000);
          type = daysDifference < 7 ? "recent" : "older";
        }

        chats.push({
          id: doc.id,
          chatId: doc.id,
          ...chatData,
          type,
        });
      });

      logger.log("chatService: Processados", chats.length, "chats");
      callback(chats);
    },
    (error) => {
      logger.error("chatService: Erro no listener:", error);
      if (errorCallback) errorCallback(error);
    },
  );
}

// Escutar mensagens de um chat (criação e atualizações de status)
export function listenToMessages(chatId, callback) {
  const messagesRef = ref(myRtdb, `messages/${chatId}`);

  const handler = (snapshot) => {
    const message = {
      id: snapshot.key,
      ...snapshot.val(),
    };
    callback(message);
  };

  onChildAdded(messagesRef, handler);
  onChildChanged(messagesRef, handler); // captura atualizações de status (sent → delivered → read)

  // Retornar função de cleanup
  return () => off(messagesRef);
}

// Enviar mensagem
export async function sendMessage(chatId, text, targetUserId = null) {
  try {
    const messagesRef = ref(myRtdb, `messages/${chatId}`);
    const currentUserId = myAuth.currentUser.uid;

    await push(messagesRef, {
      senderId: currentUserId,
      text: text,
      timestamp: serverTimestamp(),
      status: "sent",
    });

    // Track message sent
    if (targetUserId) {
      trackMessageSent(chatId, targetUserId);
    } else {
      trackMessageSent(chatId, "unknown");
    }
  } catch (error) {
    logger.error("Erro ao enviar mensagem:", error);
    throw error;
  }
}

// Disponibilidade da conexão gratuita semanal (usuários Free)
export async function getFreeConnectionStatus() {
  return axios.get(`/v1/chat/free-connection-status`);
}

// Limpar mensagens não lidas
export async function clearUnreadMessages(chatId) {
  try {
    const token = await myAuth.currentUser.getIdToken();
    await axios.post(
      `/v1/chat/${chatId}/clear-unread`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch (error) {
    logger.error("Erro ao limpar não lidas:", error);
    throw error;
  }
}
